import { GridColDef } from '@mui/x-data-grid';

declare const require: any;

interface ExportToXLSXOptions<T extends Record<string, any>> {
    rows: T[];
    columns: GridColDef[];
    includeColumns?: string[];
    additionalFields?: Array<{
        field: string;
        headerName: string;
        valueGetter?: (row: T) => string | number;
    }>;
    filename?: string;
}

export function exportToXLSX<T extends Record<string, any>>(options: ExportToXLSXOptions<T>): void {
    const {
        rows,
        columns,
        includeColumns = [],
        additionalFields = [],
        filename = 'exported-data',
    } = options;

    // Use require for XLSX
    const XLSX = require('xlsx');

    const activeColumns = includeColumns.length > 0
        ? columns.filter(col => includeColumns.includes(col.field))
        : columns;

    type ExportField = {
        field: string;
        headerName: string | undefined;
        valueGetter?: any;
    };

    const allFields: ExportField[] = [
        ...activeColumns.map(col => ({
            field: col.field,
            headerName: col.headerName || col.field,
            valueGetter: col.valueGetter,
        })),
        ...additionalFields.map(field => ({
            field: field.field,
            headerName: field.headerName,
            valueGetter: field.valueGetter,
        })),
    ];

    // Create headers
    const headers = allFields.map(col => 
        col.headerName ? col.headerName.toString() : col.field
    );

    // Create data rows
    const xlsxRows = rows.map(row => {
        const values = allFields.map(col => {
            let value: any = '';

            if (col.valueGetter && typeof col.valueGetter === 'function') {
                try {
                    const params: any = {
                        row,
                        value: row[col.field],
                        field: col.field,
                        id: row.id,
                        colDef: col as any,
                        api: undefined as any,
                        hasFocus: false,
                        tabIndex: -1,
                        formattedValue: row[col.field],
                    };

                    try {
                        value = col.valueGetter(params);
                    } catch (e) {
                        value = (col.valueGetter as (row: T) => string | number)(row);
                    }
                } catch (e) {
                    console.error(`Error getting value for field ${col.field}:`, e);
                    value = row[col.field] || '';
                }
            } else {
                value = row[col.field] || '';
            }

            if (value instanceof Date) {
                value = value.toLocaleString();
            } else if (typeof value === 'object' && value !== null) {
                value = JSON.stringify(value);
            }

            return value;
        });

        return values;
    });

    // Create worksheet data with headers and rows
    const worksheetData = [headers, ...xlsxRows];

    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

    // Auto-size columns
    const colWidths = headers.map((header, index) => {
        const maxLength = Math.max(
            header.length,
            ...xlsxRows.map(row => String(row[index] || '').length)
        );
        return { wch: Math.min(maxLength + 2, 50) }; // Max width of 50 characters
    });
    worksheet['!cols'] = colWidths;

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Timesheet');

    // Generate and download file
    XLSX.writeFile(workbook, `${filename}.xlsx`);
}
